import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Eye, Star, ArrowUpRight, ToggleLeft, ToggleRight, Loader2, RefreshCw } from "lucide-react";
import { api, formatPrice, asItems, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function TrendingLeaderboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [manualOnly, setManualOnly] = useState(false);
  const [windowDays, setWindowDays] = useState(30);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api.get(`/trending/leaderboard?limit=25&window_days=${windowDays}&manual_only=${manualOnly}`)
      .then(({ data }) => setItems(asItems(data)))
      .catch((requestError) => {
        setItems([]);
        setError(formatApiError(requestError));
      })
      .finally(() => setLoading(false));
  }, [manualOnly, windowDays]);
  useEffect(() => { load(); }, [load]);

  const toggleTrending = async (p) => {
    try {
      await api.patch(`/admin/properties/${p.property_id}/feature`, { trending: !p.trending });
      toast.success(p.trending ? "Removed from trending" : "Marked as trending");
      load();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    }
  };
  const toggleFeatured = async (p) => {
    try {
      await api.patch(`/admin/properties/${p.property_id}/feature`, { featured: !p.featured });
      toast.success(p.featured ? "Unfeatured" : "Featured");
      load();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    }
  };

  return (
    <div data-testid="trending-leaderboard-page">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Moderation · Discovery</p>
      <h1 className="font-display text-4xl font-black tracking-tighter text-primary mb-2">Trending Leaderboard</h1>
      <p className="text-sm text-muted-foreground max-w-2xl mb-8">
        Auto-ranked by total views (threshold ≥ 15). Override individual entries with the toggles below.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          value={windowDays}
          onChange={(e) => setWindowDays(Number(e.target.value))}
          className="text-sm border border-border rounded-md px-3 py-2 bg-background"
          data-testid="lb-window"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <button
          type="button"
          onClick={() => setManualOnly(v => !v)}
          aria-pressed={manualOnly}
          title="Show only manually overridden properties"
          className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-md border transition ${
            manualOnly ? "border-primary bg-primary text-primary-foreground" : "border-border text-primary hover:bg-secondary"
          }`}
          data-testid="lb-manual-only"
        >
          {manualOnly ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
          Manual override only
        </button>
        <span className="text-xs text-muted-foreground ml-auto">{items.length} entries</span>
      </div>

      <div className="bg-background border border-border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[780px]">
          <thead className="bg-secondary">
            <tr>
              {["#", "Property", "Location", "Price", "Views", "Status", "Override"].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground"><span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-primary" /> Loading leaderboard…</span></td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={7} className="px-4 py-10 text-center" role="alert"><p className="text-sm font-semibold text-foreground">Leaderboard could not be loaded.</p><p className="mt-1 text-xs text-muted-foreground">{error}</p><Button type="button" size="sm" variant="outline" className="mt-3 gap-2" onClick={load}><RefreshCw className="h-3.5 w-3.5" aria-hidden="true"/>Retry</Button></td></tr>
            )}
            {!loading && !error && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">No properties match.</td></tr>
            )}
            {!loading && !error && items.map((p, i) => (
              <tr key={p.property_id} className="border-t border-border" data-testid={`lb-row-${p.property_id}`}>
                <td className="px-4 py-3 font-display font-black text-lg text-primary tabular-nums">{i + 1}</td>
                <td className="px-4 py-3">
                  <Link to={`/properties/${p.property_id}`} className="flex items-center gap-3 group">
                    <div className="w-12 h-12 rounded-md bg-secondary overflow-hidden shrink-0">
                      {p.images?.[0] && <img src={p.images[0]} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                    </div>
                    <span className="font-semibold text-primary group-hover:underline inline-flex items-center gap-1">
                      {p.title} <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                    </span>
                  </Link>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{p.locality}, {p.city}</td>
                <td className="px-4 py-3 font-bold tabular-nums">{formatPrice(p.price)}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 text-sm font-bold tabular-nums">
                    <Eye className="w-3.5 h-3.5 text-muted-foreground" /> {p.views || 0}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {p.trending && (
                      <span className="inline-flex items-center gap-1 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                        <Flame className="w-3 h-3" /> Trending
                      </span>
                    )}
                    {p.featured && (
                      <span className="inline-flex items-center gap-1 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                        <Star className="w-3 h-3" /> Featured
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => toggleTrending(p)} title={p.trending ? "Remove trending override" : "Mark property as trending"} className="text-xs gap-1" data-testid={`lb-trend-${p.property_id}`}>
                      <Flame className={`w-3 h-3 ${p.trending ? "fill-current text-orange-500" : ""}`} /> {p.trending ? "Untrend" : "Trend"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleFeatured(p)} title={p.featured ? "Remove featured status" : "Feature this property"} className="text-xs gap-1" data-testid={`lb-feat-${p.property_id}`}>
                      <Star className={`w-3 h-3 ${p.featured ? "fill-current text-amber-500" : ""}`} /> {p.featured ? "Unfeature" : "Feature"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
